import React, { useEffect, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import Button from "../buttons/Button";
import InputBox from "../input/InputBox";
import { verifyMyPassword } from "../../../api/auth/user";

/**
 * Re-confirm password before a destructive action — same auth gate as
 * the Delete-Patient flow. Re-uses the logged-in user's own password
 * (verified server-side via verifyMyPassword) so a walk-up tampering
 * attempt on an unattended tablet can't proceed.
 *
 * Usage:
 *   <PasswordConfirmModal
 *     open={confirmOpen}
 *     onClose={() => setConfirmOpen(false)}
 *     title="Delete this invoice?"
 *     message="Invoice INV-123"
 *     onConfirm={handleActualDelete}
 *   />
 *
 * `onConfirm` is invoked only after the password verifies. If it
 * returns a Promise the modal will stay disabled (loading) until it
 * settles, so the caller can keep the modal open while the delete
 * request is in flight.
 */
const PasswordConfirmModal = ({
  open,
  onClose,
  title = "Are you sure?",
  message,
  confirmLabel = "Yes",
  cancelLabel = "No",
  onConfirm,
}) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset modal state every time it closes so a future open doesn't
  // inherit a stale password / error from the previous attempt.
  useEffect(() => {
    if (!open) {
      setPassword("");
      setError("");
      setLoading(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!password) {
      setError("Please enter your password to confirm.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await verifyMyPassword(password);
    } catch (err) {
      setLoading(false);
      setError(
        err?.response?.data?.message || "Invalid password. Try again.",
      );
      return;
    }
    try {
      await onConfirm?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper open={open} handleClose={onClose}>
      <div className="flex flex-col gap-5 items-center w-[340px] max-w-full">
        <div className="text-bodySRB text-center text-darkgrey">
          {title}
          {message && (
            <span className="block text-bodyBB pt-1 text-darkgrey">
              {message}
            </span>
          )}
        </div>
        <div className="w-full">
          <InputBox
            title={"Enter your password to confirm"}
            type={"password"}
            placeholder={"Your login password"}
            className={"h-[48px]"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError("");
            }}
            error={error || undefined}
          />
        </div>
        <div className="flex gap-2 w-full">
          <Button
            onClick={onClose}
            type={"danger"}
            className={"text-heading2B"}
            action={"button"}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            type={"yes"}
            className={"text-heading2B"}
            action={"button"}
            loading={loading}
            disabled={!password}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default PasswordConfirmModal;
